import apiClient from './api';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.verdionix.com/api';

class AIService {
  // ─── Non-streaming (returns full analysis text) ────────────────────────────

  async getComplianceHealth() {
    try {
      const response = await apiClient.get('/ai-insights/compliance-health');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch compliance health analysis.';
    }
  }

  async getPortfolioInsight() {
    try {
      const response = await apiClient.get('/ai-insights/portfolio');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch portfolio insight.';
    }
  }

  async getOperatorRisk(operatorId) {
    try {
      const response = await apiClient.get(`/ai-insights/operator-risk/${operatorId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch operator risk analysis.';
    }
  }

  async getBatchQuality(batchId) {
    try {
      const response = await apiClient.get(`/ai-insights/batch-quality/${batchId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch batch quality analysis.';
    }
  }

  // ─── Streaming via SSE (fetch + ReadableStream) ────────────────────────────
  // Uses fetch (not EventSource) so we can attach the Bearer token.
  // onChunk(delta: string) — called for each streamed token
  // onDone()               — called when stream ends
  // Returns an AbortController so the caller can cancel early.

  _stream(path, onChunk, onDone, onError) {
    const controller = new AbortController();
    const token = localStorage.getItem('authToken');

    fetch(`${API_BASE_URL}/${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          onError && onError(`HTTP ${res.status}`);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete last line

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const json = line.slice(5).trim();
            if (!json) continue;
            try {
              const parsed = JSON.parse(json);
              if (parsed.done) {
                onDone && onDone();
                return;
              }
              if (parsed.delta) {
                onChunk && onChunk(parsed.delta);
              }
            } catch {
              // malformed chunk — skip
            }
          }
        }
        onDone && onDone();
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          onError && onError(err.message);
        }
      });

    return controller;
  }

  streamComplianceHealth(onChunk, onDone, onError) {
    return this._stream('ai-insights/stream/compliance-health', onChunk, onDone, onError);
  }

  streamPortfolio(onChunk, onDone, onError) {
    return this._stream('ai-insights/stream/portfolio', onChunk, onDone, onError);
  }

  streamOperatorRisk(operatorId, onChunk, onDone, onError) {
    return this._stream(`ai-insights/stream/operator-risk/${operatorId}`, onChunk, onDone, onError);
  }

  streamBatchQuality(batchId, onChunk, onDone, onError) {
    return this._stream(`ai-insights/stream/batch-quality/${batchId}`, onChunk, onDone, onError);
  }
}

export default new AIService();
